import { IconButton } from '@mui/material';
import {
  getGridDateOperators,
  getGridStringOperators,
  GridColDef,
} from '@mui/x-data-grid-pro';

import { IcDotSpread } from '@/assets/images';
import { excludeValues, formatDate } from '@/components/DataGrid/utils';
import { Badge } from '@/components/TailwindComponents/base/Badge';

const deviceId = (): Partial<GridColDef> => ({
  sortable: false,
  renderCell: (params) => params.row.device.uid || '-',
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty', 'isAnyOf']),
  ),
});

const deviceOri = (): Partial<GridColDef> => ({
  sortable: false,
  renderCell: (params) => params.row.device.oriNumber || '-',
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty', 'isAnyOf']),
  ),
});

const mnemonic = (): Partial<GridColDef> => ({
  sortable: false,
  renderCell: (params) => params.row.device.mnemonic || '-',
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty', 'isAnyOf']),
  ),
});

const firstName = (): Partial<GridColDef> => ({
  sortable: false,
  renderCell: (params) => params.row.user.firstName || '-',
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty', 'isAnyOf']),
  ),
});

const lastName = (): Partial<GridColDef> => ({
  sortable: false,
  renderCell: (params) => params.row.user.lastName || '-',
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty', 'isAnyOf']),
  ),
});

const email = (): Partial<GridColDef> => ({
  sortable: false,
  renderCell: (params) => params.row.user.email || '-',
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty', 'isAnyOf']),
  ),
});

const badgeNumber = (): Partial<GridColDef> => ({
  sortable: false,
  renderCell: (params) =>
    params.row.user.profile.employmentInformation.badgeNumber || '-',
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty', 'isAnyOf']),
  ),
});

const department = (): Partial<GridColDef> => ({
  sortable: false,
  renderCell: (params) =>
    params.row.user.profile.employmentInformation.department || '-',
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty', 'isAnyOf']),
  ),
});

const rank = (): Partial<GridColDef> => ({
  sortable: false,
  renderCell: (params) =>
    params.row.user.profile.employmentInformation.rank || '-',
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty', 'isAnyOf']),
  ),
});

const status = (): Partial<GridColDef> => ({
  sortable: false,
  renderCell: (params) => {
    const statusValue = params.row.status || '-';

    if (statusValue === '-') {
      return statusValue;
    }

    // Map status values to badge variants
    const getStatusVariant = (statusText: string) => {
      switch (statusText.toLowerCase()) {
        case 'pending':
          return 'bg-amber-300';
        case 'approved':
          return 'bg-teal-300';
        case 'rejected':
          return 'bg-red-600';
        default:
          return 'bg-amber-300';
      }
    };

    return (
      <Badge className={getStatusVariant(statusValue)}>{statusValue}</Badge>
    );
  },
  filterable: false,
});

const createdDate = (): Partial<GridColDef> => ({
  sortable: false,
  filterOperators: getGridDateOperators(true),
  renderCell: (params) => formatDate(params.row.createdAt || '-'),
  valueGetter: (_, row) => row.createdDate,
});

const actions = (
  openMenu?: (event: React.MouseEvent<HTMLButtonElement>, param: any) => void,
): Partial<GridColDef> => ({
  sortable: false,
  renderCell: (params) =>
    params.row.status.toLowerCase() === 'pending' ? (
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          openMenu?.(e, params.row);
        }}
      >
        <IcDotSpread />
      </IconButton>
    ) : null,
});

export const ncicSearchRequestsCols = (
  openMenu?: (event: React.MouseEvent<HTMLButtonElement>, param: any) => void,
) => ({
  deviceId,
  deviceOri,
  mnemonic,
  firstName,
  lastName,
  email,
  badgeNumber,
  department,
  rank,
  status,
  createdDate,
  actions: () => actions(openMenu),
});
